import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Svg, Rect } from '@react-pdf/renderer';
import logo from './Assets/ToiceLogo.png';

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF'
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #178B9B',
    paddingBottom: 12
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8
  },
  logoImage: {
    height: 36,
    width: 'auto'
  },
  logo: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#178B9B'
  },
  subtitle: {
    fontSize: 10,
    color: '#64748b',
    marginBottom: 15
  },
  reportTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginTop: 10
  },
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 10,
    backgroundColor: '#f1f5f9',
    padding: 8
  },
  addressBox: {
    backgroundColor: '#f8fafc',
    padding: 15,
    marginBottom: 15,
    borderRadius: 4,
    border: '1 solid #e2e8f0'
  },
  addressText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a'
  },
  scoreContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 10
  },
  scoreCard: {
    flex: 1,
    padding: 15,
    backgroundColor: '#f8fafc',
    border: '1 solid #e2e8f0',
    borderRadius: 4
  },
  scoreLabel: {
    fontSize: 9,
    color: '#64748b',
    marginBottom: 5,
    textTransform: 'uppercase'
  },
  scoreValue: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 3
  },
  scoreCritical: {
    color: '#dc2626'
  },
  scoreHigh: {
    color: '#ea580c'
  },
  scoreModerate: {
    color: '#f59e0b'
  },
  scoreLow: {
    color: '#10b981'
  },
  riskBadge: {
    fontSize: 10,
    padding: '4 8',
    borderRadius: 3,
    alignSelf: 'flex-start'
  },
  table: {
    marginBottom: 15
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    padding: 8,
    fontWeight: 'bold',
    fontSize: 10
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1 solid #f1f5f9',
    fontSize: 9
  },
  tableCell: {
    flex: 1
  },
  tableCell2: {
    flex: 2
  },
  issueCategory: {
    fontWeight: 'bold',
    color: '#0f172a'
  },
  text: {
    marginBottom: 5,
    lineHeight: 1.5,
    color: '#334155'
  },
  bulletPoint: {
    flexDirection: 'row',
    marginBottom: 5
  },
  bullet: {
    width: 15,
    color: '#178B9B'
  },
  bulletText: {
    flex: 1,
    lineHeight: 1.5,
    color: '#334155'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTop: '1 solid #e2e8f0',
    paddingTop: 10,
    fontSize: 8,
    color: '#64748b',
    textAlign: 'center'
  },
  disclaimer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#fef3c7',
    border: '1 solid #fbbf24',
    borderRadius: 4
  },
  disclaimerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#92400e',
    marginBottom: 5
  },
  disclaimerText: {
    fontSize: 8,
    color: '#92400e',
    lineHeight: 1.4
  }
});

const BuildingReportPDF = ({ building, riskData }) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get approved improvements
  const approvedImprovements = building.improvements ?
    building.improvements.filter(imp => imp.status === 'approved' || imp.status === 'completed') : [];

  // Group reviews by category
  const categoryStats = {};
  building.reviews.forEach(review => {
    const category = review.category || 'Uncategorized';
    if (!categoryStats[category]) {
      categoryStats[category] = { total: 0, outstanding: 0, avgRating: 0, ratings: [] };
    }
    categoryStats[category].total++;
    categoryStats[category].ratings.push(review.rating || 3);

    // Check if this issue is outstanding (not resolved by approved improvement)
    const hasApprovedFix = approvedImprovements.some(imp => {
      const impCategory = imp.category || '';
      const impDesc = imp.description || '';
      const reviewCategory = review.category || '';
      return impCategory.toLowerCase().includes(reviewCategory.toLowerCase()) ||
             impDesc.toLowerCase().includes(reviewCategory.toLowerCase());
    });

    if (!hasApprovedFix) {
      categoryStats[category].outstanding++;
    }
  });

  // Calculate average ratings
  Object.keys(categoryStats).forEach(category => {
    const ratings = categoryStats[category].ratings;
    categoryStats[category].avgRating = (
      ratings.reduce((sum, r) => sum + r, 0) / ratings.length
    ).toFixed(1);
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <Image src={logo} style={styles.logoImage} />
          </View>
          <Text style={styles.subtitle}>Real Tenant Data • Risk Analytics</Text>
          <Text style={styles.reportTitle}>Building Risk Snapshot Report</Text>
          <Text style={[styles.subtitle, { marginTop: 3 }]}>Generated: {currentDate}</Text>
        </View>

        {/* Address */}
        <View style={styles.addressBox}>
          <Text style={styles.addressText}>{building.address}</Text>
        </View>

        {/* Key Metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Risk Score Summary</Text>
          <View style={styles.scoreContainer}>
            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Toice Risk Score</Text>
              <Text style={[
                styles.scoreValue,
                riskData.riskScore >= 80 ? styles.scoreCritical :
                riskData.riskScore >= 60 ? styles.scoreHigh :
                riskData.riskScore >= 40 ? styles.scoreModerate :
                styles.scoreLow
              ]}>
                {riskData.riskScore}
              </Text>
              <View style={[
                styles.riskBadge,
                {
                  backgroundColor: riskData.riskLevel === 'CRITICAL' ? '#fee2e2' :
                                   riskData.riskLevel === 'HIGH' ? '#fed7aa' :
                                   riskData.riskLevel === 'MODERATE' ? '#fef3c7' : '#d1fae5',
                  color: riskData.riskLevel === 'CRITICAL' ? '#991b1b' :
                         riskData.riskLevel === 'HIGH' ? '#9a3412' :
                         riskData.riskLevel === 'MODERATE' ? '#92400e' : '#065f46'
                }
              ]}>
                <Text>{riskData.riskLevel}</Text>
              </View>
            </View>

            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Total Issues</Text>
              <Text style={styles.scoreValue}>{riskData.totalIssues}</Text>
              <Text style={[styles.scoreLabel, { marginTop: 5 }]}>All reported issues</Text>
            </View>

            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Outstanding</Text>
              <Text style={[styles.scoreValue, styles.scoreHigh]}>{riskData.outstandingIssues}</Text>
              <Text style={[styles.scoreLabel, { marginTop: 5 }]}>Not yet resolved</Text>
            </View>

            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Resolved</Text>
              <Text style={[styles.scoreValue, styles.scoreLow]}>{riskData.resolvedIssues}</Text>
              <Text style={[styles.scoreLabel, { marginTop: 5 }]}>Fixed by management</Text>
            </View>

            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Critical Issues</Text>
              <Text style={[styles.scoreValue, styles.scoreCritical]}>{riskData.criticalIssues}</Text>
              <Text style={[styles.scoreLabel, { marginTop: 5 }]}>High severity</Text>
            </View>

            <View style={styles.scoreCard}>
              <Text style={styles.scoreLabel}>Avg Remediation</Text>
              <Text style={[styles.scoreValue, riskData.avgRemediationTime !== 'N/A' && riskData.avgRemediationTime <= 7 ? styles.scoreLow : riskData.avgRemediationTime <= 14 ? styles.scoreModerate : styles.scoreHigh]}>
                {riskData.avgRemediationTime !== 'N/A' ? riskData.avgRemediationTime : 'N/A'}
              </Text>
              <Text style={[styles.scoreLabel, { marginTop: 5 }]}>{riskData.avgRemediationTime !== 'N/A' ? 'Days to fix' : 'No data'}</Text>
            </View>
          </View>
        </View>

        {/* Issues by Category */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Issues by Category</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCell2}>Category</Text>
              <Text style={styles.tableCell}>Total</Text>
              <Text style={styles.tableCell}>Outstanding</Text>
              <Text style={styles.tableCell}>Avg Rating</Text>
            </View>
            {Object.entries(categoryStats)
              .sort((a, b) => b[1].total - a[1].total)
              .map(([category, stats]) => (
                <View key={category} style={styles.tableRow}>
                  <Text style={[styles.tableCell2, styles.issueCategory]}>{category}</Text>
                  <Text style={styles.tableCell}>{stats.total}</Text>
                  <Text style={[styles.tableCell, { color: stats.outstanding > 0 ? '#dc2626' : '#10b981' }]}>
                    {stats.outstanding}
                  </Text>
                  <Text style={styles.tableCell}>{stats.avgRating} ★</Text>
                </View>
              ))}
          </View>
        </View>

        {/* What This Means */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Risk Analysis</Text>
          <View style={styles.bulletPoint}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>
              This building has a risk score of {riskData.riskScore}, classified as {riskData.riskLevel} risk.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>
              {riskData.totalIssues} tenant-verified issues have been reported, with {riskData.criticalIssues} classified as critical.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>
              The issues-per-unit ratio is {riskData.issuesPerUnit}, which factors into the overall risk assessment.
            </Text>
          </View>
          {building.improvements && building.improvements.length > 0 && (
            <View style={styles.bulletPoint}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>
                {building.improvements.length} improvement(s) have been documented by management.
              </Text>
            </View>
          )}
        </View>

        {/* Improvements */}
        {building.improvements && building.improvements.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Documented Improvements</Text>
            {building.improvements.slice(0, 5).map((improvement, idx) => (
              <View key={idx} style={{ marginBottom: 15 }}>
                <View style={styles.bulletPoint}>
                  <Text style={styles.bullet}>✓</Text>
                  <Text style={styles.bulletText}>
                    {improvement.description || 'Improvement documented'}
                    {improvement.status && ` (${improvement.status})`}
                  </Text>
                </View>
                <View style={{ marginLeft: 15, marginTop: 8 }}>
                  {improvement.imageUrl ? (
                    <Image
                      src={improvement.imageUrl}
                      style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 4, border: '1 solid #e2e8f0' }}
                    />
                  ) : (
                    <View style={{ width: 200, height: 150, backgroundColor: '#f1f5f9', borderRadius: 4, border: '1 solid #e2e8f0', justifyContent: 'center', alignItems: 'center' }}>
                      <Text style={{ fontSize: 8, color: '#94a3b8', textAlign: 'center' }}>
                        [Photo Placeholder]{'\n'}Property manager can upload{'\n'}improvement evidence here
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Geographic Risk Comparison */}
        <View style={styles.section} break>
          <Text style={styles.sectionTitle}>Geographic Risk Comparison</Text>
          <Text style={[styles.text, { marginBottom: 15 }]}>
            How this property compares to nearby buildings:
          </Text>
          {building.areaComparison ? (
            <View>
              {/* Multi-radius comparison grid */}
              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 15 }}>
                <View style={{ flex: 1, backgroundColor: '#f8fafc', padding: 12, borderRadius: 4, border: '2 solid #178B9B' }}>
                  <Text style={{ fontSize: 9, color: '#64748b', marginBottom: 5 }}>This Property</Text>
                  <Text style={{
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: riskData.riskScore >= 70 ? '#dc2626' : riskData.riskScore >= 50 ? '#ea580c' : '#10b981'
                  }}>
                    {riskData.riskScore}
                  </Text>
                </View>
                {building.areaComparison.quarterMileCount > 0 && (
                  <View style={{ flex: 1, backgroundColor: '#f8fafc', padding: 12, borderRadius: 4, border: '1 solid #e2e8f0' }}>
                    <Text style={{ fontSize: 9, color: '#64748b', marginBottom: 5 }}>1/4 Mile Avg</Text>
                    <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#0f172a' }}>
                      {building.areaComparison.quarterMileAvg}
                    </Text>
                    <Text style={{ fontSize: 8, color: '#64748b', marginTop: 3 }}>
                      {building.areaComparison.quarterMileCount} buildings
                    </Text>
                  </View>
                )}
                <View style={{ flex: 1, backgroundColor: '#f8fafc', padding: 12, borderRadius: 4, border: '1 solid #e2e8f0' }}>
                  <Text style={{ fontSize: 9, color: '#64748b', marginBottom: 5 }}>1-Mile Radius Avg</Text>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#0f172a' }}>
                    {building.areaComparison.oneMileAvg}
                  </Text>
                  <Text style={{ fontSize: 8, color: '#64748b', marginTop: 3 }}>
                    {building.areaComparison.oneMileCount} buildings
                  </Text>
                </View>
                <View style={{ flex: 1, backgroundColor: '#f8fafc', padding: 12, borderRadius: 4, border: '1 solid #e2e8f0' }}>
                  <Text style={{ fontSize: 9, color: '#64748b', marginBottom: 5 }}>3-Mile Radius Avg</Text>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#0f172a' }}>
                    {building.areaComparison.threeMileAvg}
                  </Text>
                  <Text style={{ fontSize: 8, color: '#64748b', marginTop: 3 }}>
                    {building.areaComparison.threeMileCount} buildings
                  </Text>
                </View>
              </View>

              {/* Comparison insight */}
              <View style={{ backgroundColor: riskData.riskScore < building.areaComparison.oneMileAvg ? '#d1fae5' : '#fee2e2', padding: 10, borderRadius: 4, marginBottom: 15 }}>
                <Text style={{ fontSize: 9, color: riskData.riskScore < building.areaComparison.oneMileAvg ? '#065f46' : '#991b1b' }}>
                  {riskData.riskScore < building.areaComparison.oneMileAvg
                    ? `✓ This property has a lower risk score than the 1-mile radius average (${Math.abs(riskData.riskScore - building.areaComparison.oneMileAvg)} points better).`
                    : `⚠ This property has a higher risk score than the 1-mile radius average (${Math.abs(riskData.riskScore - building.areaComparison.oneMileAvg)} points worse).`
                  }
                </Text>
              </View>

              {/* 10 Closest Buildings Table */}
              {building.areaComparison.nearbyBuildings && building.areaComparison.nearbyBuildings.length > 0 && (
                <View>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#0f172a', marginBottom: 10 }}>
                    10 Closest Buildings Comparison
                  </Text>
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <Text style={[styles.tableCell, { flex: 0.5 }]}>#</Text>
                      <Text style={styles.tableCell2}>Address</Text>
                      <Text style={styles.tableCell}>Distance</Text>
                      <Text style={styles.tableCell}>Risk Score</Text>
                    </View>
                    {building.areaComparison.nearbyBuildings.slice(0, 10).map((nearby, idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { flex: 0.5, color: '#64748b' }]}>{idx + 1}</Text>
                        <Text style={[styles.tableCell2, { fontSize: 8 }]}>{nearby.address}</Text>
                        <Text style={styles.tableCell}>{nearby.distance} mi</Text>
                        <Text style={[
                          styles.tableCell,
                          {
                            color: nearby.riskScore >= 70 ? '#dc2626' :
                                   nearby.riskScore >= 50 ? '#ea580c' :
                                   nearby.riskScore >= 30 ? '#f59e0b' : '#10b981',
                            fontWeight: 'bold'
                          }
                        ]}>
                          {nearby.riskScore}
                        </Text>
                      </View>
                    ))}
                  </View>
                  <View style={{ backgroundColor: '#f8fafc', padding: 10, borderRadius: 4, border: '1 solid #e2e8f0', marginTop: 10 }}>
                    <Text style={{ fontSize: 9, color: '#64748b' }}>
                      Average of 10 closest buildings: {building.areaComparison.tenClosestAvg} (This property: {riskData.riskScore})
                    </Text>
                  </View>
                </View>
              )}
            </View>
          ) : (
            <View style={{ backgroundColor: '#f8fafc', padding: 15, borderRadius: 4, border: '1 solid #e2e8f0' }}>
              <Text style={{ fontSize: 9, color: '#64748b', textAlign: 'center' }}>
                Geographic comparison data requires location coordinates. Enable location tracking on reviews
                to unlock neighborhood benchmarking and risk comparison analytics.
              </Text>
            </View>
          )}
        </View>

        {/* Risk Score Trend */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Risk Score Trend</Text>
          <Text style={[styles.text, { marginBottom: 15 }]}>
            Quarterly risk score progression:
          </Text>
          {building.quarterlyScores && building.quarterlyScores.length > 0 ? (
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 150, gap: 10, marginBottom: 10 }}>
              {building.quarterlyScores.map((quarter, idx) => {
                const barHeight = (quarter.score / 100) * 130;
                const barColor = quarter.score >= 70 ? '#dc2626' : quarter.score >= 50 ? '#ea580c' : '#10b981';
                return (
                  <View key={idx} style={{ flex: 1, alignItems: 'center' }}>
                    <View style={{ height: 130, justifyContent: 'flex-end', width: '100%', alignItems: 'center' }}>
                      <View style={{ width: '70%', height: barHeight, backgroundColor: barColor, borderRadius: 4 }} />
                    </View>
                    <Text style={{ fontSize: 8, marginTop: 5, textAlign: 'center' }}>{quarter.label}</Text>
                    <Text style={{ fontSize: 10, fontWeight: 'bold', marginTop: 2 }}>{quarter.score}</Text>
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={{ backgroundColor: '#f8fafc', padding: 15, borderRadius: 4, border: '1 solid #e2e8f0' }}>
              <Text style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginBottom: 8 }}>
                Current Risk Score: {riskData.riskScore}
              </Text>
              <Text style={{ fontSize: 8, color: '#94a3b8', textAlign: 'center' }}>
                Quarterly trend data will be available after 90 days of monitoring.
              </Text>
            </View>
          )}
        </View>

        {/* Issue Photos */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Issue Documentation Photos</Text>
          <Text style={[styles.text, { marginBottom: 15 }]}>
            Tenant-submitted photos documenting reported issues:
          </Text>
          {building.reviews && building.reviews.some(r => r.imageUrl) ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {building.reviews
                .filter(review => review.imageUrl)
                .slice(0, 6)
                .map((review, idx) => (
                  <View key={idx} style={{ width: '48%', marginBottom: 10 }}>
                    <Image
                      src={review.imageUrl}
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, border: '1 solid #e2e8f0' }}
                    />
                    <Text style={{ fontSize: 8, color: '#64748b', marginTop: 4 }}>
                      {review.category} - Rating: {review.rating}/5
                    </Text>
                  </View>
                ))}
            </View>
          ) : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {[1, 2, 3, 4].map((idx) => (
                <View key={idx} style={{ width: '48%', marginBottom: 10 }}>
                  <View style={{ width: '100%', height: 120, backgroundColor: '#f1f5f9', borderRadius: 4, border: '1 dashed #cbd5e1', justifyContent: 'center', alignItems: 'center' }}>
                    <Text style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>
                      [Issue Photo {idx}]{'\n'}Tenant photo will appear here
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text style={styles.disclaimerTitle}>Report Disclaimer</Text>
          <Text style={styles.disclaimerText}>
            This report is based on tenant-submitted data and Toice's proprietary risk scoring algorithm.
            Data is anonymized and aggregated. This report should be used as one component of comprehensive
            due diligence and does not constitute a property inspection, environmental assessment, or
            professional recommendation. Toice Risk Intelligence makes no warranties regarding the completeness
            or accuracy of tenant-submitted information.
          </Text>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>© {new Date().getFullYear()} Toice Risk Intelligence • Confidential Report</Text>
          <Text style={{ marginTop: 3 }}>Generated by Toice Risk Intelligence Platform</Text>
        </View>
      </Page>
    </Document>
  );
};

export default BuildingReportPDF;
